import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withTranslation } from 'react-i18next';
import Backdrop from '@getgo/chameleon-react/dist/Backdrop';
import ChameleonLoader from 'Components/Common/ChameleonLoader';
import is from 'is_js';
import styles from './styles.scss';
import { fetchAsset, discardVideo } from '../../../Reducks/VideoEditor/actions';
import { fetchTranscript, discardTranscript } from '../../../Reducks/Transcript/actions';
import { connectDispatch } from '../../../Utils/decorators';
import { getUtterances } from '../../../Reducks/Transcript/selectors';
import { generateVttFromStt } from '../../../Utils/generateVttFromStt';

const mapStateToProps = (state) => ({
  accountKey: state.user.metadata.accountKey,
  video: state.videoEditor.video,
  utterances: getUtterances(state)
});

@connectDispatch(mapStateToProps)
class ContentViewer extends React.PureComponent {
  static propTypes = {
    canDownloadMedia: PropTypes.bool,
    children: PropTypes.any,
    isOnlyAudio: PropTypes.bool,
    isOnlyImage: PropTypes.bool,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    previewUrl: PropTypes.string,
    title: PropTypes.string,
    views: PropTypes.number,
    isLoading: PropTypes.bool,
    t: PropTypes.func,
    assetKey: PropTypes.string,
    assetVersion: PropTypes.string,
    accountKey: PropTypes.string,
    dispatch: PropTypes.func,
    video: PropTypes.object,
    utterances: PropTypes.object,
    isPreview: PropTypes.bool
  }

  static defaultProps = {
    canDownloadMedia: false,
    isOnlyAudio: false,
    isOnlyImage: false,
    isOpen: true,
    views: 0,
    isPreview: false
  }

  stopPropagation(e) {
    e.stopPropagation();
  }

  componentDidMount() {
    const { assetKey, assetVersion, accountKey, dispatch } = this.props;
    if (assetKey && assetVersion && accountKey) {
      dispatch(fetchAsset(assetKey, assetVersion, accountKey));
    }
  }

  componentDidUpdate(prevProps) {
    const { assetKey, assetVersion, accountKey, video, dispatch } = this.props;
    if (prevProps.assetKey !== assetKey) {
      dispatch(fetchAsset(assetKey, assetVersion, accountKey));
    }
    if (video && prevProps.video !== video) {
      if (video.transcript) {
        dispatch(fetchTranscript(video.transcript));
      } else if (prevProps.video.transcript) {
        dispatch(discardTranscript());
      }
    }
  }

  componentWillUnmount() {
    if (this.props.isPreview) {
      return;
    }
    this.props.dispatch(discardVideo());
    this.props.dispatch(discardTranscript());
  }

  displayContentViewer() {
    const { previewUrl, isOnlyAudio, canDownloadMedia, isOnlyImage, t, video, utterances } = this.props;

    const downloadOption = canDownloadMedia ? 'download' : 'nodownload';
    const transcriptfile = generateVttFromStt(utterances);

    if (!previewUrl) {
      return null;
    }

    if (isOnlyImage) {
      return (
        <img className={styles.imageViewer} src={previewUrl} alt='Handout' />
      );
    }

    if (isOnlyAudio) {
      return (
        <audio controls autoPlay controlsList={downloadOption}>
          <source src={previewUrl} type="audio/mpeg" />
          {t('video.noHtml5')}
        </audio>
      );
    }

    return (
      <video controls autoPlay controlsList={downloadOption}>
        <source src={previewUrl} type="video/mp4" />
        {!is.mobile() && video.transcript && (
          <track
            kind="subtitles"
            src={transcriptfile}
            srcLang="en"
            label="English"
          />
        )}
        {t('video.noHtml5')}
      </video>
    );
  }

  renderLoading() {
    return (
      <Backdrop className={styles.backdrop} isOpen={this.props.isOpen}>
        <ChameleonLoader />
      </Backdrop>
    );
  }

  render() {
    const { isLoading } = this.props;
    if (isLoading) { return this.renderLoading(); }
    const { title, views, isOpen, isOnlyAudio, t } = this.props;

    const mediaTopStyle = classNames({
      [styles.audioTop]: isOnlyAudio,
      [styles.videoTop]: !isOnlyAudio
    });

    return (
      <Backdrop onClickHandler={this.props.onClose} isOpen={isOpen}>
        {/* eslint-disable-next-line */}
        <div className={classNames(styles.main, mediaTopStyle)} onClick={this.stopPropagation}>
          <button onClick={this.props.onClose}>
            <span className={styles.closeButton}>{t('button.close')}</span><i className="togo-icon togo-icon-closes" />
          </button>
          {this.props.children || this.displayContentViewer()}
          <div className={styles.contentDetails}>
            <span className={styles.contentTitle}>{title}</span>
            <span>{views && views >= 0 ? `${views} ${t('cards.recordings.views')}` : null}</span>
          </div>
        </div>
      </Backdrop>
    );
  }
}

export default withTranslation()(ContentViewer);
